import moment from 'moment'
import { memo, useMemo } from 'react'
import { useIntl } from 'react-intl'

import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { SHORT_TIME_HOUR_FORMAT_AM_PM } from 'constants/dateFormats'
import { rem } from 'polished'
import styled from 'styled-components'
import type { TripItem } from 'tripPlanner/types/tripItem'
import type { ItinerarySection } from 'tripPlanner/utils/itinerary'
import clsx from 'clsx'
import { getItemPinCategory, getItemPinIcon } from 'tripPlanner/components/CleanCard/common/itemPinTypes'

const Container = styled(Group)`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
  padding: ${rem(12)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  gap: ${rem(12)};

  @container (min-width: 640px) {
    padding: ${rem(16)} ${rem(48)} ${rem(16)} ${rem(28)};
    gap: ${rem(16)};
  }

  &.vertical-gap {
    gap: 0;

    @container (min-width: 640px) {
      gap: ${rem(4)};
    }
  }
`

interface Props {
  intermediateItem: TripItem
  date?: ItinerarySection
}

function hasEndDate(item: TripItem): item is Extract<TripItem, { endDate?: moment.Moment }> {
  return 'endDate' in item && item.endDate !== undefined
}

function ItineraryInfoCard({ intermediateItem, date }: Props) {
  const intl = useIntl()
  const category = useMemo(() => getItemPinCategory(intermediateItem.type), [intermediateItem.type])
  const IconComponent = getItemPinIcon(category)

  if (!hasEndDate(intermediateItem)) {
    return null
  }

  const startDate = moment(intermediateItem.startDate)
  const endDate = moment(intermediateItem.endDate)
  const totalDays = endDate.diff(startDate, 'days') + 1

  let currentDay: number | undefined
  if (date && moment.isMoment(date)) {
    currentDay = date.diff(startDate, 'days') + 1
  }

  const isOnStartDate = date && moment.isMoment(date) && startDate.isSame(date, 'day')
  const isOnEndDate = date && moment.isMoment(date) && endDate.isSame(date, 'day')
  const hasStartPlace = 'startPlace' in intermediateItem
  const hasEndPlace = 'endPlace' in intermediateItem
  const showDropOffTime = hasEndPlace && isOnEndDate

  let itemName: string | undefined
  let containerDir: 'horizontal' | 'vertical' | 'horizontal-reverse' | 'vertical-reverse' = 'horizontal'
  if (intermediateItem.type === 'ACCOMMODATION' && intermediateItem.place?.name) {
    itemName = intermediateItem.place.name
  } else if ('name' in intermediateItem && intermediateItem.name) {
    itemName = intermediateItem.name
  } else if (hasStartPlace && intermediateItem.startPlace?.name && isOnStartDate) {
    itemName = intermediateItem.startPlace.name
  } else if (hasEndPlace && intermediateItem.endPlace?.name && isOnEndDate) {
    itemName = intl.formatMessage(
      {
        id: 'ibF_cqlj65i5b4W55H4E',
        defaultMessage: 'Drop off at {placeName}',
        description: 'Title for a car rental drop off location in the itinerary',
      },
      { placeName: intermediateItem.endPlace.name },
    )
    containerDir = 'vertical'
  }

  if (!itemName) {
    return null
  }

  return (
    <Container direction={containerDir} verticalAlign="center" className={clsx({ 'vertical-gap': containerDir === 'vertical' })}>
      <Group direction="horizontal" gap={12} verticalAlign="center">
        <IconComponent size="XS" />
        <BodyText variant="medium" weight="bold" as="span">
          {itemName}
        </BodyText>
      </Group>
      {showDropOffTime && (
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <BodyText variant="small" colour="neutral-two" as="span">
            {intl.formatMessage(
              {
                id: 'WIAGmBfcIWmnPok1Lp9g',
                defaultMessage: 'Drop off at {time}',
                description: 'Drop off time label for a car rental in the itinerary',
              },
              { time: endDate.format(SHORT_TIME_HOUR_FORMAT_AM_PM) },
            )}
          </BodyText>
        </Group>
      )}
      {!showDropOffTime && currentDay !== undefined && totalDays > 0 && (
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <BodyText variant="small" colour="neutral-two" as="span">
            {intl.formatMessage(
              {
                id: '841gKAwDZQiGEOvU7PFp',
                defaultMessage: 'Day {currentDay} of {totalDays}',
                description: 'Day progress label showing current day number out of total days in itinerary',
              },
              { currentDay, totalDays },
            )}
          </BodyText>
        </Group>
      )}
    </Container>
  )
}

export default memo(ItineraryInfoCard)
